/**
 * Keeping the economic calendar live without polling (ECONOMIC_CALENDAR_DATA_PLAN.md §4): a page loads the
 * calendar once, then the WebSocket `calendar` channel sends the releases that changed in each new build. A
 * missed build (a gap in versions) or a reconnect loads it again; while the connection is down, the page asks
 * every 15 minutes.
 */
import { useEffect, useRef } from 'react';
import type { CalendarUpdate, EconCalendar } from '@platform/shared';
import { useWsStatus } from './ws';

/**
 * Apply one update to a calendar copy. Releases outside the copy's window are ignored. Returns the same copy when
 * the update is already in it, and null when builds were missed and the copy must be loaded again.
 */
export function mergeCalendar(cal: EconCalendar, u: CalendarUpdate): EconCalendar | null {
  if (cal.version !== undefined) {
    if (u.v <= cal.version) return cal;
    if (u.v > cal.version + 1) return null;
  }
  const from = Date.parse(cal.from);
  const to = Date.parse(cal.to);
  const byId = new Map(cal.data.map((e) => [e.id, e]));
  for (const id of u.removed) byId.delete(id);
  for (const e of u.events) {
    const t = Date.parse(e.time);
    if (t >= from && t < to) byId.set(e.id, e);
  }
  const data = [...byId.values()].sort((a, b) => a.time.localeCompare(b.time) || b.impact - a.impact || a.event.localeCompare(b.event));
  return { ...cal, data, version: u.v, updatedAt: u.updatedAt ?? cal.updatedAt, available: cal.available || data.length > 0 };
}

/** Load again when the connection comes back, and every 15 minutes while it is down. */
export function useWsFallback(reload: () => void) {
  const status = useWsStatus();
  const ref = useRef(reload);
  ref.current = reload;
  const was = useRef(status);
  useEffect(() => {
    if (status === 'live' && was.current !== 'live') ref.current();
    was.current = status;
    if (status === 'live') return;
    const t = window.setInterval(() => ref.current(), 15 * 60_000);
    return () => window.clearInterval(t);
  }, [status]);
}
